import { useGlobalStore } from '@sunrise/shared'
import { trackLink, track, createTealiumTracking, MsTracking, NOT_SET, MLM, TrackingParameter } from '@sunrise/tracking'
import {
    AmsItem,
    AmsRequestKeys,
    AmsSearchRequest,
    AmsSearchResponse,
} from '../../../services/amsSearch/amsSearch.interface'
import { useInitializedAmsSearchStore } from '../../../store/amsSearch'
import { MlmActionIds, buildMlmData } from '../../../utils/tracking/mlm'
import pipe from 'lodash/fp/pipe'
import mapValues from 'lodash-es/mapValues'
import isArray from 'lodash-es/isArray'
import isEmpty from 'lodash-es/isEmpty'
import { onMounted, watch } from 'vue'
import { determinePriceTextBy } from '../../../components/AmsPriceIndicator/AmsPriceIndicatorHelpers'
import { replaceSpacesWithUnderscores } from '../../../utils/stringUtils/stringUtils'

export type AnchorMouseEvent = MouseEvent & { target: HTMLAnchorElement }

export const useTracking = (cmsElementName: string) => {
    const globalStore = useGlobalStoreWithRequestContext()
    const amsSearchStore = useInitializedAmsSearchStore()

    const pageUrl = globalStore.requestContext.url.href

    const isLinkClick = (event: MouseEvent): event is AnchorMouseEvent => event.target instanceof HTMLAnchorElement

    const mlm = {
        view(items: AmsItem[]) {
            items.length && mlmTrackView(items, cmsElementName, amsSearchStore.results.page, pageUrl)
        },
        viewport(itemInViewport: AmsItem) {
            mlmTrackViewPort([itemInViewport], cmsElementName, amsSearchStore.results.page, pageUrl)
        },
        click(item: AmsItem) {
            mlmTrackItemClick(item, cmsElementName, amsSearchStore.results.page, pageUrl)
        },
    }

    const initialCount = amsSearchStore.results.totalHits

    onMounted(() => {
        tealiumTrackView(TealiumState.PREDEFINED, amsSearchStore.results, initialCount)
        mlm.view(amsSearchStore.results.items)
    })

    watch(
        () => amsSearchStore.results,
        (newValue) => {
            tealiumTrackView(TealiumState.USERDEFINED, newValue, initialCount)
            mlm.view(newValue.items)
        },
    )

    const tealium = {
        click: tealiumTrackItemClick,
        priceIndicatorClick: tealiumTrackPriceIndicatorClick,
    }

    const track = {
        view: mlm.view,
        viewport: mlm.viewport,
        itemClick(item: AmsItem, event: MouseEvent) {
            if (!isLinkClick(event)) {
                throw new Error('Target should be HTMLAnchorElement')
            }
            mlm.click(item)
            tealium.click(item, event)
        },
        onPriceIndicatorClick(item: AmsItem, isOpened: boolean) {
            tealium.priceIndicatorClick(item, isOpened)
        },
    }

    return { track }
}

enum TealiumTrackingIds {
    CLICK = 'ed0000036',
    PI_OPEN = 'ed0000068',
    PI_CLOSE = 'ed0000069',
}

export enum TealiumState {
    PREDEFINED = 'predefined',
    USERDEFINED = 'userdefined',
}

const tealiumTrackView = (state: TealiumState, amsResponse: AmsSearchResponse, inititalCount: number) => {
    const tealiumData = buildTealiumViewTracking(
        amsResponse.request,
        state,
        inititalCount,
        amsResponse.totalHits,
        amsResponse.items,
    )
    track(tealiumData)
}

function buildTealiumItemTracking(item: AmsItem): MsTracking {
    return createTealiumTracking({
        mslayer_element_company_name: item.advertiser,
        mslayer_element_id: item.externalId,
        mslayer_element_position: item.position.toString(),
        mslayer_element_text: item.title,
        mslayer_element_variant: 'default',
        mslayer_element_data_id: TealiumTrackingIds.CLICK,
        mslayer_element_type_text: NOT_SET,
        mslayer_element_body_type: 'list',
        mslayer_affiliate_id: NOT_SET,
        mslayer_element_detail_start_date: NOT_SET,
    })
}

export function buildTealiumViewTracking(
    searchRequestData: AmsSearchRequest,
    state: TealiumState,
    initialTotalCount: number,
    totalCount: number,
    items: AmsItem[],
): TrackingParameter {
    const [priceMin, priceMax] = computeMinMaxPrice(items)
    const tealiumViewData = {
        mslayer_internal_searchbox_filter_element_categories: searchRequestData.bodyType,
        mslayer_internal_searchbox_filter_element_color: searchRequestData.bodyColor,
        mslayer_internal_searchbox_filter_element_doors: buildDoorString(searchRequestData),
        mslayer_internal_searchbox_filter_element_emissionClass: searchRequestData.emissionClass,
        mslayer_internal_searchbox_filter_element_equipment: searchRequestData.equipment,
        mslayer_internal_searchbox_filter_element_fuel: searchRequestData.fuel,
        mslayer_internal_searchbox_filter_element_gears: searchRequestData.transmissionType,
        mslayer_internal_searchbox_filter_element_kilometer_max: searchRequestData.mileageMax,
        mslayer_internal_searchbox_filter_element_kilometer_min: searchRequestData.mileageMin,
        mslayer_internal_searchbox_filter_element_kw_max: searchRequestData.kwMax,
        mslayer_internal_searchbox_filter_element_kw_min: searchRequestData.kwMin,
        mslayer_internal_searchbox_filter_element_onlinePurchaseOnly: searchRequestData.deliveryOption,
        mslayer_internal_searchbox_filter_element_price_max: searchRequestData.priceMax,
        mslayer_internal_searchbox_filter_element_price_min: searchRequestData.priceMin,
        mslayer_internal_searchbox_filter_element_ps_max: searchRequestData.hpMax,
        mslayer_internal_searchbox_filter_element_ps_min: searchRequestData.hpMin,
        mslayer_internal_searchbox_filter_element_radius: searchRequestData.radius,
        mslayer_internal_searchbox_filter_element_registrationdate_max: searchRequestData.firstRegistrationDateMax,
        mslayer_internal_searchbox_filter_element_registrationdate_min: searchRequestData.firstRegistrationDateMin,
        mslayer_internal_searchbox_filter_element_seats: searchRequestData.seatsMin,
        mslayer_internal_searchbox_filter_element_state: searchRequestData.offerType,
        mslayer_internal_searchbox_resultlist_price_max: priceMax,
        mslayer_internal_searchbox_resultlist_price_min: priceMin,
        mslayer_internal_searchbox_used: state,
        mslayer_internal_searchphrases_city_combo: searchRequestData.locationName,
        mslayer_list_results_after_filter: totalCount,
        mslayer_list_results_max: initialTotalCount,
        mslayer_list_sorting_logic: searchRequestData.sort,
        mslayer_page_number: searchRequestData.page,
        mslayer_internal_searchbox_filter_element_carbrand: searchRequestData.makeId,
        mslayer_internal_searchbox_filter_element_carmodel: searchRequestData.modelId,
    }

    return getAsStringObject(tealiumViewData)
}

function buildTealiumPriceIndicatorTracking(item: AmsItem, isOpened: boolean): TrackingParameter {
    return {
        mslayer_element_data_id: determinePriceIndicatorTrackingId(isOpened),
        mslayer_element_text: 'Auto_Preisbewertungsampel',
        mslayer_element_type_text: determinePriceIndicatorTypeText(isOpened),
        mslayer_element_count: NOT_SET,
        mslayer_element_variant: getPriceTrackingText(item.priceIndicator.priceClass),
        mslayer_element_body_Type: determinePriceIndicatorBodyType(isOpened),
        msplayer_element_position: item.position.toString(),
        mslayer_element_id: NOT_SET,
        mslayer_element_company_name: NOT_SET,
    }
}

const tealiumTrackPriceIndicatorClick = (item: AmsItem, isOpened: boolean) => {
    track(buildTealiumPriceIndicatorTracking(item, isOpened))
}

const tealiumTrackItemClick = (item: AmsItem, event: AnchorMouseEvent) =>
    trackLink(event, event.target, [buildTealiumItemTracking(item)])

export const mlmTrackItemClick = (item: AmsItem, componentName: string, page: number, pageUrl: string) =>
    MLM.trackClick(buildMlmData([item], componentName, page, MlmActionIds.CLICK, pageUrl))

export const mlmTrackView = (items: AmsItem[], componentName: string, page: number, pageUrl: string) =>
    MLM.trackView(buildMlmData(items, componentName, page, MlmActionIds.VIEW, pageUrl))

export const mlmTrackViewPort = (items: AmsItem[], componentName: string, page: number, pageUrl: string) =>
    MLM.trackView(buildMlmData(items, componentName, page, MlmActionIds.VIEWPORT, pageUrl))

const isGlobalStoreWithRequestContext = (
    globalStore: ReturnType<typeof useGlobalStore>,
): globalStore is RequiredNonNullable<ReturnType<typeof useGlobalStore>, 'requestContext'> =>
    !!globalStore.requestContext

const useGlobalStoreWithRequestContext = () => {
    const globalStore = useGlobalStore()
    if (isGlobalStoreWithRequestContext(globalStore)) {
        return globalStore
    }

    throw new Error('Global Store not initialized!')
}

const computeMinMaxPrice = (items: AmsItem[]) =>
    items.reduce(
        ([prevMin, prevMax], { price }) => [Math.min(prevMin, price), Math.max(prevMax, price)],
        [Infinity, -Infinity],
    )

const buildDoorString = ({
    doorsMin,
    doorsMax,
}: Pick<AmsSearchRequest, AmsRequestKeys.DoorsMin | AmsRequestKeys.DoorsMax>) =>
    doorsMin && doorsMax ? `${doorsMin}/${doorsMax}` : null

const getAsStringObject = pipe(replaceEmptyValuesWithNotSet, toString)

function replaceEmptyValuesWithNotSet(data: Nullable<TrackingParameter>): TrackingParameter {
    return mapValues(data, (value) => ((isArray(value) && isEmpty(value)) || !value ? NOT_SET : value))
}

function toString(data: TrackingParameter): TrackingParameter {
    return mapValues(data, (value) => (typeof value === 'number' ? value.toString() : value))
}

function determinePriceIndicatorTrackingId(isOpened: boolean) {
    return isOpened ? TealiumTrackingIds.PI_OPEN : TealiumTrackingIds.PI_CLOSE
}

function determinePriceIndicatorBodyType(isOpened: boolean) {
    return isOpened ? 'list' : 'modal'
}

function determinePriceIndicatorTypeText(isOpened: boolean) {
    return isOpened ? 'PBA_oeffnen' : 'PBA_schliessen'
}

const getPriceTrackingText = pipe(determinePriceTextBy, replaceSpacesWithUnderscores)
